import { useState } from 'react';
import { PrimaryButton, SecondaryButton } from '@client/new/components/button';
import { Modal } from '@client/new/components/Modal/Modal';
import { LibraryVideo } from '../videos.domain';
import { useShortVideos } from '../videos.hooks';
import { ClipsGallery } from './ClipsGallery';
import { useVideosList } from '../videos.viewModel.list';
import { FilteringAndSorting } from '../videos.view';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { TableLoader } from '@client/new/components/TableShared/TableLoader';
import { ClipGalleryLoader } from './ClipGalleryLoader';

export type AddFromVideosModalProps = {
  isOpen: boolean;
  onClose: () => void;
  media: LibraryVideo;
  onSave: (video: LibraryVideo) => void;
  influencerId: number;
};

const VideosList = (
  p: Pick<AddFromVideosModalProps, 'onSave' | 'onClose'> & {
    videos: LibraryVideo[];
  },
) => {
  const { dispatch, list, searchText, sort, sortOptions } = useVideosList(
    p.videos,
  );
  const [selectedVideo, setSelectedVideo] = useState<LibraryVideo>(p.videos[0]);

  return (
    <div className="flex flex-col max-h-full">
      <FilteringAndSorting
        searchText={searchText}
        sort={sort}
        sortOptions={sortOptions}
        dispatch={dispatch}
      />
      <div className="my-4 overflow-y-scroll flex-shrink">
        <ClipsGallery
          list={list}
          videoType="short"
          shortMaxWidth={180}
          onClick={setSelectedVideo}
          selectedClip={selectedVideo}
        />
      </div>
      <div className="flex justify-end">
        <div className="flex gap-4">
          <SecondaryButton
            style={{ backgroundColor: 'white', color: 'black' }}
            onClick={p.onClose}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={() => p.onSave(selectedVideo)}>
            Save
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

const AsyncVideosList = (p: AddFromVideosModalProps) => {
  const asyncVideos = useShortVideos({ influencerId: p.influencerId });
  const handleSaveClick = (selectedVideo: LibraryVideo) => {
    p.onClose();
    p.onSave(selectedVideo);
  };

  return (
    <AsyncRenderer
      data={asyncVideos}
      renderer={(v) => (
        <VideosList videos={v} onClose={p.onClose} onSave={handleSaveClick} />
      )}
      loadRender={
        <div className="flex flex-col gap-6">
          <TableLoader rows={2} height={60} />
          <ClipGalleryLoader width={220} />
        </div>
      }
    />
  );
};

export const AddFromVideosModal = (p: AddFromVideosModalProps) => (
  <Modal title="Add video" isOpen={p.isOpen} onClose={p.onClose}>
    <AsyncVideosList {...p} />
  </Modal>
);
